import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import Edit from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { validationTypes } from '../../helpers';
import VerificationTooltip from '../tooltips/VerificationTooltip';
import { FORM_OUTLINED_INPUT_CLASS_NAME } from '../../theme/constants/outlined-input';
import { DEFAULT_FORM_INPUT_LABEL_CLASS_NAME } from '../../theme/constants/input-label';
import { useIsMobile } from '../../hooks';

const TitledTextField = ({
  title,
  onChangeInput,
  type,
  disabledEdit,
  attributeName,
  setFormValues,
  formValues,
  errorMsg,
  setErrorMsg,
  verificationAttribute,
  totalElements,
}) => {
  const isMobile = useIsMobile('md');
  const gridSize = 12 / Math.min(totalElements, isMobile ? 2 : 4);
  const { t } = useTranslation();
  const onChange = ({ target: { value } }) => {
    if (type) {
      const { truthFunction, message } = validationTypes(value)[type];
      if (!truthFunction() && value) {
        setErrorMsg({ ...errorMsg, [type]: message });
      } else {
        setErrorMsg({ ...errorMsg, [type]: '' });
      }
    }
    onChangeInput(value);
    setFormValues({ ...formValues, [attributeName]: value });
  };
  useEffect(() => {
    setErrorMsg({});
  }, [disabledEdit]);
  return (
    <Grid item xs={12} sm={gridSize}>
      <Stack
        direction="row"
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ width: { xs: '100%', md: '90%' } }}
          spacing={0.5}
        >

          {type === 'phone' && (
            <Typography>
              +{t('Phone prefix')}
            </Typography>
          )}
          <FormControl fullWidth sx={{ width: '100%' }}>
            <InputLabel
              htmlFor={`input-${attributeName}`}
              shrink
              className={DEFAULT_FORM_INPUT_LABEL_CLASS_NAME}
              error={!!errorMsg[type]}
            >
              {title}
            </InputLabel>
            <OutlinedInput
              id={`input-${attributeName}`}
              fullWidth
              label={title}
              value={formValues[attributeName] || ''}
              disabled={disabledEdit || !onChangeInput}
              onChange={onChange}
              className={FORM_OUTLINED_INPUT_CLASS_NAME}
              sx={{ height: 48 }}
              error={!!errorMsg[type]}
              endAdornment={
                  !disabledEdit && onChangeInput && (
                    <Edit sx={{ fontSize: 18, borderRadius: 1, pl: '4px' }} color="primary" />
                  )
                }
            />
            {errorMsg[type] && (
            <FormHelperText
              error
              sx={{ position: 'absolute', bottom: -20 }}
            >
              {errorMsg[type]}
            </FormHelperText>
            )}
          </FormControl>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              height: 40,
            }}
          >
            {verificationAttribute && (
            <VerificationTooltip verificationAttribute={verificationAttribute} />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Grid>
  );
};

TitledTextField.propTypes = {
  title: PropTypes.string.isRequired,
  onChangeInput: PropTypes.func,
  type: PropTypes.string,
  disabledEdit: PropTypes.bool,
  attributeName: PropTypes.string.isRequired,
  setFormValues: PropTypes.func.isRequired,
  formValues: PropTypes.shape({
    companyName: PropTypes.string,
    companyRut: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    mailProvided: PropTypes.string,
  }).isRequired,
  errorMsg: PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  setErrorMsg: PropTypes.func.isRequired,
  verificationAttribute: PropTypes.string,
  totalElements: PropTypes.number.isRequired,
};

TitledTextField.defaultProps = {
  onChangeInput: null,
  type: null,
  disabledEdit: false,
  verificationAttribute: null,
};

export default TitledTextField;
